
import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row, Container, Label, Input, InputGroup, Button, UncontrolledAlert, Form, FormFeedback } from "reactstrap";
import axios from "axios";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";

const AddContract = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("authToken");
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const isHome = searchParams.get("h") === "true";

  // Constants
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const CONTRACT_OPTIONS = ['contract', 'mou', 'nda', 'compliance', 'permit', 'license', 'other'];
  const ALLOWED_FILE_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "text/plain",
  ];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // State Management
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState({
    countries: false,
    submission: false,
  });
  const [newContract, setNewContract] = useState({
    title: "",
    reference: "",
    country: "",
    contracting_party: "",
    effective_from: "",
    notification_period: "",
    expiry_date: "",
    created_by: "",
    document_type: "",
    status: "Active",
    file: null,
    comment: "",
  });
  const [userName, setUserName] = useState("");
  const [alerts, setAlerts] = useState({
    error: "",
    success: "",
  });
  const [validation, setValidation] = useState({});

  // Initialize component
  useEffect(() => {
    document.title = `Add ${type || "Contract"} | Onfon Group Legal - React`;
    
    // Set document type based on route params
    setNewContract(prev => ({
      ...prev,
      document_type: isHome ? "" : type
    }));
    
    // Fetch user data and countries
    fetchUserData();
    fetchCountries();
  }, [isHome, type]);

  // Fetch the logged-in user's data
  const fetchUserData = async () => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("authUser"));
      if (userData) {
        setNewContract(prev => ({
          ...prev,
          created_by: userData._id,
        }));
        setUserName(userData.username);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setAlerts(prev => ({
        ...prev,
        error: "Failed to load user data. Please try logging in again."
      }));
    }
  };

  // Fetch Countries from API
  const fetchCountries = async () => {
    setLoading(prev => ({ ...prev, countries: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/countries`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch countries");

      const data = await response.json();
      setCountries(data);
    } catch (err) {
      console.error("Error fetching countries:", err);
      setAlerts(prev => ({
        ...prev,
        error: "Failed to load countries. Please try again."
      }));
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewContract(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is updated
    if (validation[name]) {
      setValidation(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Handle Date Changes
  const handleDateChange = (date, name) => {
    setNewContract(prev => ({ ...prev, [name]: date }));
    
    // Clear validation error when field is updated
    if (validation[name]) {
      setValidation(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Handle File Upload with Validation
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setValidation(prev => ({
        ...prev,
        file: "Please upload a valid file (PDF, DOC/DOCX, JPEG, JPG, PNG, or TXT)."
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setValidation(prev => ({
        ...prev,
        file: "File size exceeds the maximum allowed limit of 5MB."
      }));
      return;
    }

    setNewContract(prev => ({ ...prev, file }));
    setValidation(prev => ({ ...prev, file: "" }));
  };

  // Validate Form Fields Before Submission
  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      "title",
      "reference",
      "country",
      "contracting_party",
      "effective_from",
      "notification_period",
      "expiry_date",
      "document_type",
      "status",
    ];

    // Check required fields
    requiredFields.forEach(field => {
      if (!newContract[field]) {
        errors[field] = `${field.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())} is required`;
      }
    });

    // Additional validations
    if (newContract.notification_period && isNaN(newContract.notification_period)) {
      errors.notification_period = "Notification period must be a number";
    }

    // Date validations
    if (newContract.effective_from && newContract.expiry_date) {
      const effectiveDate = new Date(newContract.effective_from);
      const expiryDate = new Date(newContract.expiry_date);
      
      if (effectiveDate > expiryDate) {
        errors.expiry_date = "Expiry date cannot be earlier than effective date";
      }
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setAlerts(prev => ({
        ...prev,
        error: "Please correct the errors in the form."
      }));
      return;
    }

    setLoading(prev => ({ ...prev, submission: true }));
    setAlerts({ error: "", success: "" });

    try {
      const formData = new FormData();

      // Append all form fields to FormData
      Object.keys(newContract).forEach((key) => {
        if (key === "created_by") {
          const userData = JSON.parse(sessionStorage.getItem("authUser"));
          formData.append(key, userData._id);
        } else if (key === "file" && newContract[key]) {
          formData.append(key, newContract[key]);
        } else if (newContract[key] || newContract[key] === 0) {
          formData.append(key, newContract[key]);
        }
      });

      const response = await axios.post(`${API_BASE_URL}/api/createContract`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setAlerts(prev => ({
        ...prev,
        success: "Contract created successfully!"
      }));
      
      // Reset form and navigate after success
      setTimeout(() => {
        navigate(`/contracts/${type}`);
      }, 2000);
    } catch (err) {
      console.error(`Error creating ${type}:`, err);
      let errorMessage = "An unexpected error occurred.";

      if (err.response) {
        errorMessage = err.response.data?.message || err.response.statusText;
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = err.message;
      }

      setAlerts(prev => ({
        ...prev,
        error: errorMessage
      }));
    } finally {
      setLoading(prev => ({ ...prev, submission: false }));
    }
  };

  // Render form field based on field type
  const renderFormField = (key) => {
    const isInvalid = !!validation[key];
    
    switch (key) {
      case "effective_from":
      case "expiry_date":
        return (
          <>
            <InputGroup>
              <Flatpickr
                className={`form-control d-block ${isInvalid ? 'is-invalid' : ''}`}
                placeholder="dd MM yyyy"
                options={{
                  altInput: true,
                  altFormat: "F j, Y",
                  dateFormat: "Y-m-d",
                }}
                value={newContract[key]}
                onChange={(date) => handleDateChange(date, key)}
              />
            </InputGroup>
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
          </>
        );

      case "file":
        return (
          <>
            <Input 
              type="file" 
              name="file" 
              id="file" 
              onChange={handleFileChange}
              invalid={isInvalid}
            />
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
            <small className="text-muted">
              Allowed file types: PDF, DOC, DOCX, JPG, JPEG, PNG, TXT (Max: 5MB)
            </small>
          </>
        );

      case "status":
        return (
          <>
            <Input
              type="select"
              name="status"
              value={newContract.status}
              onChange={handleChange}
              invalid={isInvalid}
            >
              <option value="Active">Active</option>
              <option value="Terminated">Terminated</option>
              <option value="Expired">Expired</option>
            </Input>
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
          </>
        );

      case "document_type":
        return isHome ? (
          <>
            <Input
              type="select"
              name="document_type"
              value={newContract.document_type}
              onChange={handleChange}
              invalid={isInvalid}
            >
              <option value="">Select Document Type</option>
              {CONTRACT_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
              ))}
            </Input>
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
          </>
        ) : (
          <>
            <Input
              type="text"
              name="document_type"
              value={newContract.document_type}
              disabled
              invalid={isInvalid}
            />
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
          </>
        );

      case "created_by":
        return (
          <Input type="text" name="created_by" value={userName} readOnly />
        );

      case "notification_period":
        return (
          <>
            <Input
              type="number"
              name="notification_period"
              value={newContract.notification_period}
              onChange={handleChange}
              invalid={isInvalid}
              min="0"
            />
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
            <small className="text-muted">Enter the notification period in days</small>
          </>
        );

      case "country":
        return (
          <>
            <Input
              type="select"
              name="country"
              value={newContract.country}
              onChange={handleChange}
              disabled={loading.countries}
              invalid={isInvalid}
            >
              <option value="">Select Country</option>
              {countries.length > 0 ? (
                countries.map((country) => (
                  <option key={country._id} value={country.name}>
                    {country.name}
                  </option>
                ))
              ) : loading.countries ? (
                <option value="">Loading countries...</option>
              ) : (
                <option value="">No countries available</option>
              )}
              <option value="multiple">Multiple</option>
            </Input>
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
          </>
        );

      case "comment":
        return (
          <>
            <Input
              type="textarea"
              name={key}
              id="comment"
              placeholder="Add any notes or comments about this contract"
              value={newContract.comment}
              onChange={handleChange}
              rows="3"
            />
            <small className="text-muted">Optional: Add any relevant notes about this contract</small>
          </>
        );

      default:
        return (
          <>
            <Input
              type="text"
              name={key}
              value={newContract[key]}
              onChange={handleChange}
              invalid={isInvalid}
              placeholder={`Enter ${key.replace("_", " ")}`}
            />
            {isInvalid && <FormFeedback>{validation[key]}</FormFeedback>}
          </>
        );
    }
  };

  // Define Form Fields and their display order
  const formFields = [
    "title",
    "reference",
    "country",
    "contracting_party",
    "effective_from",
    "notification_period",
    "expiry_date",
    "document_type",
    "status",
    "created_by",
    "file",
    "comment",
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs 
            title="Forms" 
            breadcrumbItem={`Add New ${type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Contract'}`} 
          />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  {alerts.error && (
                    <UncontrolledAlert color="danger">
                      {alerts.error}
                    </UncontrolledAlert>
                  )}
                  {alerts.success && (
                    <UncontrolledAlert color="success">
                      {alerts.success}
                    </UncontrolledAlert>
                  )}
                  
                  <Form onSubmit={handleSubmit}>
                    {formFields.map((key) => (
                      <Row className="mb-3" key={key}>
                        <Label htmlFor={key} className="col-md-2 col-form-label">
                          {key
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                          {key === "comment" && (
                            <span className="text-muted ms-1">(Optional)</span>
                          )}
                        </Label>
                        <div className="col-md-10">
                          {renderFormField(key)}
                        </div>
                      </Row>
                    ))}
                    
                    <div className="d-flex justify-content-start mt-4">
                      <Button 
                        color="primary" 
                        type="submit"
                        disabled={loading.submission}
                      >
                        {loading.submission ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Submitting...
                          </>
                        ) : (
                          `Add ${type || 'Contract'}`
                        )}
                      </Button>
                      <Button 
                        color="secondary" 
                        tag={Link} 
                        to={`/contracts/${type}`} 
                        className="ms-2"
                        disabled={loading.submission}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddContract;
